import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { ChangeDetectorRef, Component, forwardRef, Input, OnInit } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { merge, Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { v4 as uuidv4 } from 'uuid';

import { ModuleItemAvailableSubType, ModuleItemSubType } from '../../../../../../models/module-item-sub-type';
import { CalculatedVariable } from '../../../../../../models/calculated-variable';
import { StudyTranslation } from '../../../../../../models/study-translation';
import { Language, LanguageCode } from '../../../../../../models/language';
import { UploadIds, UploadKeys } from '../../../../../../models/upload';
import { ModuleItem } from '../../../../../../models/module-item';
import { Study } from '../../../../../../models/study';
import { Module } from '../../../../../../models/module';
import {
  MODULE_ITEM_CHOICES_IDENTIFIER,
  MODULE_ITEM_DESCRIPTION_IDENTIFIER,
  MODULE_ITEM_EMOTION_IMAGE_IDENTIFIER,
  MODULE_ITEM_GRAPH_IDENTIFIER,
  MODULE_ITEM_LABEL_IDENTIFIER,
  MODULE_ITEM_QUESTION_IDENTIFIER,
  PARTICIPANT_ITEMS,
  RANDOMIZATION_VALUES,
  TINYMCE_ITEM_DEFAULT_VALUE,
} from '../../../../../../core/dashboard-constants';
import { tinymceInitConfig } from '../../../../../../shared/components/tinymce-editor/tinymce-editor-config';
import { selectCalculatedVariables } from '../../../../../state/calculated-variable/calculated-variable.selectors';
import { selectSelectedLanguage, selectSupportedLanguages } from '../../../../../state/study/study.selectors';
import { removeWhitespaces } from '../../../../../../core/custom-form-validators';
import { ModuleItemDialogComponent } from '../../module-item-dialog.component';
import { setSelectedLanguage } from '../../../../../state/study/study.actions';
import { selectModules } from '../../../../../state/module/module.selectors';
import { StudyType } from '../../../../../../models/study-type';
import { AppState } from '../../../../../../state';
import { ModuleItemPreviewData } from '../../../../../../models/module-item-preview-data';
import { PreviewService } from '../../../../../services/preview/preview.service';
import { ModuleItemAvailableAttribute } from '../../../../../../models/module-item-attribute';
import { ModuleItemAvailableType } from '../../../../../../models/module-item-type';
import { Category } from '../../../../../../models/category';
import { selectCategories } from '../../../../../state/category/category.selectors';

@Component({
  selector: 'dashboard-module-item-core',
  templateUrl: './module-item-core.component.html',
  styleUrls: ['./module-item-core.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ModuleItemCoreComponent),
      multi: true,
    },
  ],
})
export class ModuleItemCoreComponent implements OnInit, ControlValueAccessor {
  @Input() dialogData;

  moduleItemCoreForm: FormGroup;

  selectedStudy: Study;
  selectedModule: Module;
  moduleItemSubType: ModuleItemSubType;
  selectedModuleItem: ModuleItem;
  isDuplicate: boolean;

  supportedLanguages: Language[];
  selectedLanguage: Language;
  calculatedVariablesForModule: CalculatedVariable[];
  modulesForStudy: Module[];
  categories: Category[];

  isEdit: boolean;

  isImageToEdit: boolean;
  imgPath: string;
  idsForUpload: UploadIds;
  keyForUpload = UploadKeys.UPLOAD_FROM_MODULE_ITEM;

  isVideoInputVisible = false;

  MODULE_ITEM_QUESTION_IDENTIFIER = MODULE_ITEM_QUESTION_IDENTIFIER;
  MODULE_ITEM_DESCRIPTION_IDENTIFIER = MODULE_ITEM_DESCRIPTION_IDENTIFIER;
  MODULE_ITEM_GRAPH_IDENTIFIER = MODULE_ITEM_GRAPH_IDENTIFIER;

  RANDOMIZATION_VALUES = RANDOMIZATION_VALUES;

  StudyType = StudyType;
  ModuleItemAvailableType = ModuleItemAvailableType;
  ModuleItemAvailableSubType = ModuleItemAvailableSubType;

  type = '';
  subType = '';

  formSubmitted = false;

  tinymceInitConfig = {
    ...tinymceInitConfig,
    plugins: tinymceInitConfig.plugins.concat(' dynamicanswer'),
    toolbar: tinymceInitConfig.toolbar.concat(' dynamicAnswer'),
    participant_details: PARTICIPANT_ITEMS,
    module_items: [TINYMCE_ITEM_DEFAULT_VALUE],
  };

  tinymceInitConfigGraph = {
    ...this.tinymceInitConfig,
    graph_field: true,
    toolbar: 'dynamicAnswer',
  };

  onChange;
  onTouched;

  imageUploadUuid: string;

  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private toastrService: ToastrService,
    private moduleItemDialogRef: MatDialogRef<ModuleItemDialogComponent>,
    private previewService: PreviewService,
    public changeDetectorRef: ChangeDetectorRef
  ) {}

  get label(): AbstractControl {
    return this.moduleItemCoreForm.get('label');
  }

  get categoryId(): AbstractControl {
    return this.moduleItemCoreForm.get('categoryId');
  }

  get translations(): FormArray {
    return this.moduleItemCoreForm.get('translations') as FormArray;
  }

  get imagePath(): AbstractControl {
    return this.moduleItemCoreForm.get('imagePath');
  }

  get videoUrl(): AbstractControl {
    return this.moduleItemCoreForm.get('videoUrl');
  }

  ngOnInit(): void {
    this.moduleItemCoreForm = this.formBuilder.group({
      label: [''],
      categoryId: [''],
      translations: this.formBuilder.array([]),
      imagePath: [''],
      videoUrl: [''],
    });

    if (
      this.subType !== ModuleItemAvailableSubType.GRAPH &&
      this.subType !== ModuleItemAvailableSubType.VIDEO_AUDIO &&
      this.subType !== ModuleItemAvailableSubType.QUADRANT
    ) {
      this.imageUploadUuid = uuidv4();
    }

    this.subscriptions.add(
      this.store
        .select(selectSupportedLanguages)
        .subscribe((supportedLanguages) => (this.supportedLanguages = supportedLanguages))
    );
    this.subscriptions.add(
      this.store.select(selectSelectedLanguage).subscribe((selectedLanguage) => {
        this.selectedLanguage = selectedLanguage;
        this.changeDetectorRef.detectChanges();
      })
    );
    this.subscriptions.add(
      this.store.select(selectModules).subscribe((modules) => {
        this.modulesForStudy = modules;
        this.tinymceInitConfig = {
          ...this.tinymceInitConfig,
          modules: this.populateModulesDropdown(this.modulesForStudy),
        };
        this.tinymceInitConfigGraph = {
          ...this.tinymceInitConfigGraph,
          modules: this.populateModulesDropdown(this.modulesForStudy),
        };
      })
    );
    this.subscriptions.add(
      this.store.select(selectCalculatedVariables).subscribe((calculatedVariablesForModule) => {
        this.calculatedVariablesForModule = calculatedVariablesForModule;
        this.tinymceInitConfig = {
          ...this.tinymceInitConfig,
          calculated_variables: this.populateCalculatedVariablesDropdown(calculatedVariablesForModule),
        };
        this.tinymceInitConfigGraph = {
          ...this.tinymceInitConfigGraph,
          calculated_variables: this.populateCalculatedVariablesDropdown(calculatedVariablesForModule),
        };
      })
    );
    this.subscriptions.add(
      this.store.select(selectCategories).subscribe((categories) => (this.categories = categories))
    );

    this.selectedStudy = this.dialogData?.selectedStudy;
    this.selectedModule = this.dialogData?.selectedModule;
    this.moduleItemSubType = this.dialogData?.moduleItemSubType;
    this.selectedModuleItem = this.dialogData?.moduleItem;
    this.isDuplicate = this.dialogData?.isDuplicate;

    this.type = this.selectedModuleItem?.type || this.moduleItemSubType?.itemType;
    this.subType = this.selectedModuleItem?.subType || this.moduleItemSubType?.type;

    this.isEdit = true;

    this.tinymceInitConfig = {
      ...this.tinymceInitConfig,
      study_type: this.selectedStudy?.type,
    };
    this.tinymceInitConfigGraph = {
      ...this.tinymceInitConfigGraph,
      study_type: this.selectedStudy?.type,
    };

    this.removeAllTranslationFields();
    if (this.isEdit || this.isDuplicate) {
      const ccLabelTranslation = this.selectedModuleItem?.translations?.find(
        (translation) =>
          translation?.language === LanguageCode.ENGLISH && translation.identifier === MODULE_ITEM_LABEL_IDENTIFIER
      );
      if (ccLabelTranslation) {
        this.label.patchValue(ccLabelTranslation?.translation);
      }
      if (this.selectedModuleItem?.imagePath) {
        this.isImageToEdit = true;
        this.imgPath = this.selectedModuleItem?.imagePath;
        this.imagePath.patchValue(this.selectedModuleItem?.imagePath);
        this.idsForUpload = {
          studyId: this.selectedStudy?.id,
          moduleId: this.selectedModule?.id,
        };
      } else {
        this.idsForUpload = {
          studyId: this.selectedStudy?.id,
          moduleId: this.selectedModule?.id,
        };
      }
      if (this.selectedModuleItem?.videoUrl) {
        this.isVideoInputVisible = true;
        this.videoUrl.setValue('https:' + this.selectedModuleItem.videoUrl.replace('https:', ''));
      }
      if (this.selectedModuleItem?.categoryId) {
        this.categoryId.setValue(this.selectedModuleItem?.categoryId);
      }
      if (this.supportedLanguages) {
        this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code));
      }

      this.previewService.setPreviewData({ imageUrl: this.imgPath, fileUploadUuid: this.imageUploadUuid });
    } else {
      this.supportedLanguages
        ? this.supportedLanguages.forEach((language) => this.createTranslationFields(language?.code))
        : this.createTranslationFields(this.selectedLanguage.code);
    }

    this.emitPreviewValues();

    this.subscriptions.add(
      merge(
        ...this.translations.controls.map((translationControl) => translationControl.valueChanges)
      ).subscribe((translationControlValue) =>
        this.previewService.setPreviewData(this.populatePreviewText(translationControlValue))
      )
    );

    this.subscriptions.add(
      this.videoUrl.valueChanges.subscribe((videoUrlValueChanges) =>
        this.previewService.setPreviewData({
          videoUrl: {
            url: videoUrlValueChanges,
            isVisible: this.isVideoInputVisible,
          },
        })
      )
    );

    this.moduleItemCoreForm.valueChanges.subscribe((value) => {
      this.onChange(value);
      this.onTouched();
    });

    if (this.selectedModule?.randomization === RANDOMIZATION_VALUES.CATEGORY) {
      this.categoryId.setValidators([Validators.required]);
    }
  }

  removeAllTranslationFields(): void {
    this.translations.clear();
  }

  createTranslationFields(languageCode): void {
    const filteredStudyTranslations = this.selectedModuleItem?.translations?.filter(
      (translation) => translation?.language === languageCode
    );

    const moduleItemStatement = this.formBuilder.group({
      id: [filteredStudyTranslations?.length ? filteredStudyTranslations[0]?.id : 0],
      language: [languageCode],
      identifier: [MODULE_ITEM_QUESTION_IDENTIFIER],
      translation: [
        filteredStudyTranslations?.length ? filteredStudyTranslations[0]?.translation : '',
        [Validators.required, removeWhitespaces],
      ],
    });

    const moduleItemDescription = this.formBuilder.group({
      id: [filteredStudyTranslations?.length ? filteredStudyTranslations[1]?.id : 0],
      language: [languageCode],
      identifier: [MODULE_ITEM_DESCRIPTION_IDENTIFIER],
      translation: [
        filteredStudyTranslations?.length ? filteredStudyTranslations[1]?.translation : '',
        [removeWhitespaces],
      ],
    });

    this.translations.push(moduleItemStatement);
    this.translations.push(moduleItemDescription);

    if (this.subType === ModuleItemAvailableSubType.GRAPH) {
      const dynamicTextTranslation = this.formBuilder.group({
        id: [filteredStudyTranslations?.length ? filteredStudyTranslations[2]?.id : 0],
        language: [languageCode],
        identifier: [MODULE_ITEM_GRAPH_IDENTIFIER],
        translation: [
          filteredStudyTranslations?.length ? filteredStudyTranslations[2]?.translation : '',
          [Validators.required, removeWhitespaces],
        ],
      });
      this.translations.push(dynamicTextTranslation);
    }

    this.writeValue(this.translations);
  }

  populateModulesDropdown(modules: Module[]): any {
    const modulesForDropdown = [{ ...TINYMCE_ITEM_DEFAULT_VALUE, moduleItems: [] }];
    modules?.map((module) => {
      if (module?.name) {
        modulesForDropdown.push({
          text: module?.name,
          value: module?.id.toString(),
          moduleItems: module?.moduleItems,
        });
      } else if (module?.enrolment) {
        modulesForDropdown.push({
          text: 'Subscription module',
          value: module?.id.toString(),
          moduleItems: module?.moduleItems,
        });
      }
    });
    return modulesForDropdown;
  }

  populateCalculatedVariablesDropdown(calculatedVariables: CalculatedVariable[]): any {
    const cvForDropdown = [TINYMCE_ITEM_DEFAULT_VALUE];
    calculatedVariables?.map((calculatedVariable) =>
      cvForDropdown.push({
        text: calculatedVariable?.uniqueName,
        value: calculatedVariable?.id.toString(),
      })
    );
    return cvForDropdown;
  }

  selectTemplateLanguage(event: MatTabChangeEvent): void {
    const foundSelected = this.supportedLanguages.find((language) => language?.name === event.tab.textLabel);
    this.store.dispatch(setSelectedLanguage({ selectedLanguage: foundSelected }));
    this.emitPreviewValues();
  }

  emitPreviewValues(): void {
    this.translations.controls.forEach((translationControl) => {
      this.previewService.setPreviewData(this.populatePreviewText(translationControl.value));
    });

    this.previewService.setPreviewData({
      imageUrl: this.imagePath.value,
      videoUrl: { url: this.videoUrl.value, isVisible: !!this.videoUrl.value },
      fileUploadUuid: this.imageUploadUuid,
    });
  }

  populatePreviewText(
    translation: StudyTranslation,
    randomize = false,
    selectedLanguageCodeFromParent?: string,
    uploadUuidFromParent?: string
  ): ModuleItemPreviewData {
    let previewQuestion = '';
    let previewDescription = '';
    let previewButtonLabel = '';
    let previewChoices = [];
    let previewYesLabel = '';
    let previewNoLabel = '';
    let previewXAxis = '';
    let previewYAxis = '';
    let previewSliderLabel = '';
    let previewLabelLeft = '';
    let previewLabelRight = '';
    let previewQuadrantImage = '';

    if (translation.language === (selectedLanguageCodeFromParent || this.selectedLanguage?.code)) {
      if (translation.identifier === MODULE_ITEM_QUESTION_IDENTIFIER) {
        previewQuestion = translation.translation;
      }
      if (translation.identifier === MODULE_ITEM_DESCRIPTION_IDENTIFIER) {
        previewDescription = translation.translation;
      }
      if (translation.identifier === ModuleItemAvailableAttribute.BUTTON_LABEL) {
        previewButtonLabel = translation.translation;
      }
      if (translation.identifier === MODULE_ITEM_CHOICES_IDENTIFIER) {
        previewChoices = translation.translation.split('\n').filter((row) => row);
      }
      if (translation.identifier === ModuleItemAvailableAttribute.YES_LABEL) {
        previewYesLabel = translation.translation;
      }
      if (translation.identifier === ModuleItemAvailableAttribute.NO_LABEL) {
        previewNoLabel = translation.translation;
      }
      if (translation.identifier === ModuleItemAvailableAttribute.X_AXIS) {
        previewXAxis = translation.translation;
      }
      if (translation.identifier === ModuleItemAvailableAttribute.Y_AXIS) {
        previewYAxis = translation.translation;
      }
      if (translation.identifier === ModuleItemAvailableAttribute.SLIDER_LABEL) {
        previewSliderLabel = translation.translation;
      }
      if (translation.identifier === ModuleItemAvailableAttribute.LABEL_LEFT) {
        previewLabelLeft = translation.translation;
      }
      if (translation.identifier === ModuleItemAvailableAttribute.LABEL_RIGHT) {
        previewLabelRight = translation.translation;
      }
      if (translation.identifier === MODULE_ITEM_EMOTION_IMAGE_IDENTIFIER) {
        previewQuadrantImage = translation.translation;
        this.imageUploadUuid = uploadUuidFromParent;
      }
    }

    return {
      question: previewQuestion,
      description: previewDescription,
      buttonLabel: previewButtonLabel,
      choices: randomize ? this.randomizeChoices(previewChoices) : previewChoices,
      yesLabel: previewYesLabel,
      noLabel: previewNoLabel,
      xAxis: previewXAxis,
      yAxis: previewYAxis,
      sliderLabel: previewSliderLabel,
      labelLeft: previewLabelLeft,
      labelRight: previewLabelRight,
      quadrantImageUrl: previewQuadrantImage,
      fileUploadUuid: this.imageUploadUuid,
    };
  }

  toggleVideoInput(event: MatSlideToggleChange): void {
    this.isVideoInputVisible = event.checked;
    this.previewService.setPreviewData({
      videoUrl: {
        url: this.videoUrl?.value,
        isVisible: this.isVideoInputVisible,
      },
    });
  }

  randomizeChoices(choices: any): any {
    return choices.sort(() => Math.random() - 0.5);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    if (value) {
      this.moduleItemCoreForm.patchValue(value);
    }
  }
}
